import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Sidebar';
import Header from '../Header';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import BrokerageDetailsModal from './BrokerageDetailsModal';

const Settings = () => {
  const { user } = useAuth();
  const { can, isSuperAdmin } = usePermissions();
  const navigate = useNavigate();
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [isBrokerageDetailsModalOpen, setIsBrokerageDetailsModalOpen] = useState(false);

  const settingsButtons = [
    {
      name: 'Email Configuration',
      description: 'Configure SMTP settings for system emails',
      icon: '✉️',
      onClick: () => navigate('/email-settings'),
      color: 'bg-blue-50 hover:bg-blue-100 border-blue-200',
      featureKey: 'settings',
    },
    {
      name: 'Brokerage Details',
      description: 'Update company and contact information',
      icon: '🏢',
      onClick: () => setIsBrokerageDetailsModalOpen(true),
      color: 'bg-green-50 hover:bg-green-100 border-green-200',
      featureKey: 'brokerage_details',
    },
  ];

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  const visibleButtons = settingsButtons.filter((button) => {
    // Super admin always sees everything
    if (isSuperAdmin) return true;
    // Otherwise, gate on the button's feature key
    return can(button.featureKey, 'read');
  });

  const canAccessSettings = isSuperAdmin || can('settings', 'read');

  return (
    <>
      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      <div className={`transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Settings"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
          showClientNumber={false}
        />

        <div className="min-h-screen bg-gray-50 p-4 md:p-6">
          <div className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
                Settings
              </h1>
              <p className="text-gray-600 mt-2">
                Configure system settings and company information
              </p>
            </div>

            {!canAccessSettings ? (
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                <p className="text-gray-500">You do not have permission to access Settings.</p>
              </div>
            ) : visibleButtons.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-8 text-center">
                <p className="text-gray-500">No settings options are available for your role.</p>
              </div>
            ) : (
              <>
                <div className="space-y-4">
                  {visibleButtons.map((button, index) => (
                    <button
                      key={index}
                      onClick={button.onClick}
                      className={`w-full flex items-start justify-between p-6 border rounded-lg transition-all duration-200 shadow-sm ${button.color}`}
                    >
                      <div className="flex items-start">
                        <span className="text-3xl mr-4">{button.icon}</span>
                        <div className="text-left">
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {button.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {button.description}
                          </p>
                        </div>
                      </div>
                      <svg
                        className="w-5 h-5 text-gray-400 mt-1"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  ))}
                </div>

                <div className="mt-12 p-6 bg-white border border-gray-200 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">
                    About Settings
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Use the settings above to configure your system's email server and update your brokerage information.
                    These settings are used throughout the system for communications and company documentation.
                  </p>
                  <div className="grid grid-cols-2 gap-4 text-sm text-gray-500">
                    <div>
                      <span className="font-medium">Email Configuration:</span>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        <li>SMTP server settings</li>
                        <li>Email authentication</li>
                        <li>From address and name</li>
                      </ul>
                    </div>
                    <div>
                      <span className="font-medium">Brokerage Details:</span>
                      <ul className="mt-1 list-disc list-inside space-y-1">
                        <li>Company information</li>
                        <li>Contact details</li>
                        <li>Banking information</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <BrokerageDetailsModal
        isOpen={isBrokerageDetailsModalOpen}
        onClose={() => setIsBrokerageDetailsModalOpen(false)}
        brokerId={user?.broker_id ?? undefined}
      />
    </>
  );
};

export default Settings;